import React, { useState, useEffect, useCallback } from 'react';
import { webhookLogsAPI } from '../services/api';
import './WebhookLogs.css';

const WebhookLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    eventType: '',
    messageId: '',
    email: '',
    startDate: '',
    endDate: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      const response = await webhookLogsAPI.getLogs(params);
      setLogs(response.data.data || []);
      setPagination(prev => response.data.pagination || prev);
    } catch (err) {
      console.error('Failed to fetch webhook logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchLogs();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchLogs]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      'processed': '#007bff',
      'delivered': '#28a745',
      'open': '#17a2b8',
      'click': '#ffc107',
      'bounce': '#dc3545',
      'dropped': '#6c757d',
      'spamreport': '#fd7e14',
      'unsubscribe': '#e83e8c'
    };
    return colors[eventType] || '#6c757d';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="webhook-logs">
      <div className="webhook-logs-header">
        <h1 className="page-title">Webhook Logs</h1>
        <div className="webhook-logs-controls">
          <button 
            className="btn btn-secondary" 
            onClick={fetchLogs}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <label style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Event Type</label>
            <input
              type="text"
              name="eventType"
              value={filters.eventType}
              onChange={handleFilterChange}
              placeholder="e.g., delivered, open, click"
            />
          </div>
          <div className="filter-group">
            <label>Message ID</label>
            <input
              type="text"
              name="messageId"
              value={filters.messageId}
              onChange={handleFilterChange}
              placeholder="SendGrid message ID"
            />
          </div>
          <div className="filter-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={filters.email}
              onChange={handleFilterChange}
              placeholder="Recipient email"
            />
          </div>
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
        </div>
      </div>

      {/* Logs Table */}
      {logs.length === 0 ? (
        <div className="card">
          <p>No webhook logs found.</p>
        </div>
      ) : (
        <>
          <div className="card">
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Received At</th>
                    <th>Event Type</th>
                    <th>Message ID</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatDate(log.receivedAt)}</td>
                      <td>
                        <span 
                          className="badge"
                          style={{ backgroundColor: getEventTypeColor(log.eventType) }}
                        >
                          {log.eventType}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                        {log.messageId || 'N/A'}
                      </td>
                      <td>{log.email || 'N/A'}</td>
                      <td>
                        <span className={`badge ${
                          log.status === 'delivered' || log.status === 'sent' ? 'badge-success' :
                          log.status === 'opened' || log.status === 'clicked' ? 'badge-info' :
                          'badge-danger'
                        }`}>
                          {log.status || 'N/A'}
                        </span>
                      </td>
                      <td>
                        {log.processed ? (
                          <span className="badge badge-success">✓</span>
                        ) : (
                          <span className="badge badge-warning">Pending</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                        >
                          {selectedLog?.id === log.id ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}

          {/* Selected Log Details */}
          {selectedLog && (
            <div className="card" style={{ marginTop: '1.5rem' }}>
              <h3>Webhook Event Details</h3>
              <div className="log-details">
                <div className="detail-row">
                  <strong>ID:</strong> {selectedLog.id}
                </div>
                <div className="detail-row">
                  <strong>Event Type:</strong> {selectedLog.eventType}
                </div>
                <div className="detail-row">
                  <strong>Message ID:</strong> {selectedLog.messageId || 'N/A'}
                </div>
                <div className="detail-row">
                  <strong>Email:</strong> {selectedLog.email || 'N/A'}
                </div>
                <div className="detail-row">
                  <strong>Status:</strong> {selectedLog.status || 'N/A'}
                </div>
                <div className="detail-row">
                  <strong>Received At:</strong> {formatDate(selectedLog.receivedAt)}
                </div>
                <div className="detail-row">
                  <strong>Processed:</strong> {selectedLog.processed ? 'Yes' : 'No'}
                </div>
                {selectedLog.errorMessage && (
                  <div className="detail-row">
                    <strong style={{ color: '#dc3545' }}>Error:</strong> {selectedLog.errorMessage}
                  </div>
                )}
                <div className="detail-row">
                  <strong>Event Data:</strong>
                  <pre style={{
                    background: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    marginTop: '0.5rem'
                  }}>
                    {JSON.stringify(selectedLog.eventData, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WebhookLogs;

